import { useNavigate } from 'react-router-dom';
import type { AskResponse, AskAnswerItem, AskMoveBrief, AlertSeverity } from '../types';
import ProvenanceChip from './ProvenanceChip';
import { severityBand } from './severity';

// One query-log entry on /ask: the operator's ask + its deterministic answer card. Render boundary
// (spec 11.4): the matched category shows in plain language (never "parsed via …"), severity uses the
// shared four-band scale, every figure carries a provenance chip, and a decline shows only the plain
// sentence + shortcut screens — no status/parse machinery ("declined", "· none", "no match"). The
// answer text and every number come from the deterministic surface; nothing here is model-written.

const SEV_CLASS: Record<string, string> = {
  existential: 'red', high: 'amber', elevated: 'gold', watch: 'muted',
};

function fmtDollar(range?: [number, number] | null): string | null {
  if (!range) return null;
  const [low, high] = range;
  const f = (n: number) => (Number.isInteger(n) ? `${n}` : n.toFixed(1));
  return low === high ? `$${f(high)}M` : `$${f(low)}–${f(high)}M`;
}

// ProvenanceChip already leads with "Estimate"; keep only a distinguishing basis (e.g. public-data
// calibrated) so the chip doesn't read "Estimate · modeled estimate".
function chipBasis(prov?: string | null): string | undefined {
  if (!prov) return undefined;
  const cleaned = prov.replace(/^modeled\s*·?\s*/i, '').replace(/^estimate\b/i, '').trim();
  return cleaned && cleaned.toLowerCase() !== 'estimate' ? cleaned : undefined;
}

export default function AskAnswerCard({ response }: { response: AskResponse }) {
  const navigate = useNavigate();
  const { question, result, result_kind, surface_route, parsed_intent } = response;
  const items = result.items ?? [];
  const declined = result_kind === 'declined';
  const interpreted = parsed_intent.via === 'genai'; // the parse edge ran -> show its reading for confirmation
  const goTo = (route?: string | null) => { if (route) navigate(route); };

  return (
    <div className="panel">
      {/* The ask. A query-log entry (ask + answer card), never a chat turn. */}
      <div className="small faint" style={{ marginBottom: 4 }}>You asked</div>
      <div className="small" style={{ fontWeight: 600, marginBottom: 10, lineHeight: 1.4 }}>{question}</div>

      {/* Matched category, plain language. Decline shows nothing here (no status/parse line). */}
      {!declined && result.intent_label && (
        <div className="small" style={{ marginBottom: 8 }}>
          {interpreted ? <>I read this as: <b>{result.intent_label}</b></> : <>Showing: <b>{result.intent_label}</b></>}
        </div>
      )}

      <div className="small" style={{ lineHeight: 1.5, marginBottom: items.length || result.move_brief ? 10 : 6 }}>
        {String(result.summary ?? '')}
      </div>

      {items.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 10 }}>
          {items.map((item, i) => <AnswerItem key={i} item={item} goTo={goTo} />)}
        </div>
      )}

      {result.move_brief && <MoveBrief brief={result.move_brief} goTo={goTo} />}

      {result.alternative && (
        <button
          type="button"
          className="btn ghost"
          style={{ minHeight: 'auto', padding: '4px 10px', fontSize: 12, marginBottom: 8 }}
          onClick={() => goTo(result.alternative?.route)}
        >
          See {result.alternative.label} instead →
        </button>
      )}

      {/* Decline: closest screens as navigation buttons — no machinery, just where to go next. */}
      {declined && result.suggested_surfaces && result.suggested_surfaces.length > 0 && (
        <div style={{ marginTop: 4 }}>
          <div className="small faint" style={{ marginBottom: 6 }}>Try one of these screens:</div>
          <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
            {result.suggested_surfaces.map((s) => (
              <button
                key={s.route}
                type="button"
                className="btn ghost"
                style={{ minHeight: 'auto', padding: '4px 10px', fontSize: 12 }}
                onClick={() => goTo(s.route)}
              >
                {s.label} →
              </button>
            ))}
          </div>
        </div>
      )}

      {surface_route && !result.move_brief && (
        <button
          type="button"
          className="btn primary"
          style={{ minHeight: 'auto', padding: '6px 12px', fontSize: 13 }}
          onClick={() => goTo(surface_route)}
        >
          Open →
        </button>
      )}
    </div>
  );
}

function AnswerItem({ item, goTo }: { item: AskAnswerItem; goTo: (r?: string | null) => void }) {
  const dollar = fmtDollar(item.dollar_usd_m);
  const clickable = Boolean(item.route);
  return (
    <button
      type="button"
      onClick={() => goTo(item.route)}
      disabled={!clickable}
      style={{
        textAlign: 'left', background: 'var(--bg)', border: '1px solid var(--line)',
        borderRadius: 8, padding: '8px 10px', cursor: clickable ? 'pointer' : 'default', width: '100%',
      }}
    >
      <div className="row" style={{ gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        {item.severity && (
          <span className={`tag ${SEV_CLASS[item.severity] ?? 'muted'}`} style={{ fontSize: 10 }}>
            {severityBand(item.severity as AlertSeverity).label}
          </span>
        )}
        <span className="small" style={{ fontWeight: 600 }}>{item.label}</span>
        {dollar && <span className="small" style={{ color: 'var(--amber)' }}>{dollar}</span>}
        {dollar && <ProvenanceChip basis={chipBasis(item.provenance)} />}
        {clickable && <span className="small faint" style={{ marginLeft: 'auto' }}>→</span>}
      </div>
      {item.detail && <div className="small faint" style={{ marginTop: 3, lineHeight: 1.4 }}>{item.detail}</div>}
      {!dollar && item.provenance && (
        <div style={{ marginTop: 4 }}><ProvenanceChip basis={chipBasis(item.provenance)} /></div>
      )}
    </button>
  );
}

function MoveBrief({ brief, goTo }: { brief: AskMoveBrief; goTo: (r?: string | null) => void }) {
  const dollar = fmtDollar(brief.consequence?.revenue_at_risk_usd_m ?? null);
  return (
    <div style={{ border: '1px solid var(--line)', borderRadius: 8, padding: '10px 12px', marginBottom: 10, background: 'var(--bg)' }}>
      <div className="row" style={{ gap: 6, alignItems: 'center', marginBottom: 6 }}>
        <span className="tag amber" style={{ fontSize: 10, letterSpacing: '0.06em' }}>MOVE BRIEF</span>
        {brief.severity && (
          <span className={`tag ${SEV_CLASS[brief.severity] ?? 'muted'}`} style={{ fontSize: 10 }}>
            {severityBand(brief.severity as AlertSeverity).label}
          </span>
        )}
        {brief.owner && <span className="small faint" style={{ marginLeft: 'auto' }}>{brief.owner}</span>}
      </div>
      <div className="small" style={{ fontWeight: 600, marginBottom: 6, lineHeight: 1.45 }}>{brief.recommendation}</div>
      {dollar && (
        <div className="row" style={{ gap: 6, alignItems: 'center', marginBottom: 6 }}>
          <span className="small" style={{ color: 'var(--amber)' }}>{dollar} exposed</span>
          <ProvenanceChip />
        </div>
      )}
      {brief.evidence?.length > 0 && (
        <ul style={{ margin: '0 0 6px', paddingLeft: 16 }}>
          {brief.evidence.slice(0, 4).map((line, i) => <li key={i} className="small faint" style={{ lineHeight: 1.4 }}>{line}</li>)}
        </ul>
      )}
      {/* Blocked alternatives, with the blocking rule named (spec: name the veto). */}
      {brief.blocked?.length > 0 && (
        <div style={{ marginBottom: 6 }}>
          {brief.blocked.map((line, i) => (
            <div key={i} className="small" style={{ color: 'var(--red)', lineHeight: 1.4 }}>Off the table: {line}</div>
          ))}
        </div>
      )}
      <button
        type="button"
        className="btn primary"
        style={{ minHeight: 'auto', padding: '6px 12px', fontSize: 13 }}
        onClick={() => goTo(brief.disposition_route)}
      >
        Open Move Brief to dispose →
      </button>
    </div>
  );
}
